import { Injectable } from '@angular/core';
import { NgForm } from '@angular/forms';
import { IRequestCallback, RequestService } from '@df/api/request/request.service';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Status } from '@df/core/status';
import { Paypal } from '@df/module-paypal/paypal.namespace';

@Injectable({ providedIn: 'root' })
export class PaypalExpressService {
    constructor(private request: RequestService) {}
    /**
     * Loads paypal SDK script
     * @returns {Promise<undefined>}
     */
    loadSDK(status?: Status): Promise<any> {
        return this.request.send(status, () => {
            return new Promise((resolve, reject) => {
                this.request.send(
                    undefined,
                    { path: Paypal.EXPRESS_ENDPOINTS.buttonConfig },
                    result => {
                        this.request.loadScript(result.sdkUrl, { onLoad: resolve, onError: reject });
                    },
                    reject,
                    { method: 'GET' }
                );
            });
        });
    }

    /**
     * Returns token that should be returned by createOrder Paypal SDK Button method in button config
     */
    getToken(status?: Status): Promise<string | undefined> {
        return new Promise((resolve, reject) => {
            this.request.send(
                status,
                { path: Paypal.EXPRESS_ENDPOINTS.tokenData },
                (result: Paypal.IExpressTokenResult) => resolve(result.token),
                reject,
                { method: 'GET', withCredentials: true }
            );
        });
    }

    /**
     * Returns Paypal SDK "buttons" method
     */
    getButtons(): Paypal.ISDKButtons | undefined {
        return typeof window === 'undefined' ? undefined : window.paypal?.Buttons;
    }

    /**
     * Authorise transaction, it is not the end of checkout way, on success customer lands
     * on order review page where shipping method can be set, and order can be confirmed
     */

    authorise(
        status: Status,
        data: Paypal.IExpressAuthorisationData,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<void> {
        return this.request.send(status, { path: Paypal.EXPRESS_ENDPOINTS.authorization }, onSuccess, onError, {
            params: data
        });
    }

    /*
     * Loads Order review data needed on review page, review page lets customer review items,
     * select shipping method, and confirm order.
     *
     * Please note that shipping method cost may vary, so we do not confirm the exact amount of money on paypal side,
     * there we only confirm an estimate between mininal and maximal value, that may vary depending on shipping cost.
     *
     * The final charged value is confirmed on review page.
     */
    loadReviewData(status: Status, onSuccess?: IRequestCallback, onError?: IRequestCallback): Promise<Paypal.IExpressReviewData> {
        return this.request.send(status, { path: Paypal.EXPRESS_ENDPOINTS.reviewData }, onSuccess, onError, {
            method: 'GET',
            withCredentials: true
        });
    }

    /**
     * Allows to save shipping method, used on review page
     */
    saveShippingMethod(
        status: Status,
        method: string,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<Partial<Paypal.IExpressReviewData>> {
        return this.request.send(undefined, { data: { method }, path: Paypal.EXPRESS_ENDPOINTS.saveShippingMethod }, onSuccess, onError);
    }

    /**
     * Submits order once review is done, if succeded,
     * order is submitted and customer lands on standard checkout order success page.
     */
    submitOrder(
        form: NgForm,
        status: Status,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): void | Promise<Checkout.ISubmissionResponse> {
        return this.request.submitForm(form, status, { path: Paypal.EXPRESS_ENDPOINTS.submitOrder }, onSuccess, onError);
    }
}
